import React, { useState, useEffect } from 'react';
import { TestPlan, Feature, Component, Platform, PlatformItem, QuestionAnswer, TestCase } from '../../types';
import { apiRequest } from '../../utils/api';
import { showTempMessage, AppMessage, MessageSetter } from '../../utils/messages';
import { formatDurationHHMMSS, parseDurationHHMMSS } from '../../utils/date';
import { useMilestones } from '../../utils/milestones';
import AddComponentModal from '../sessions/AddComponentModal';
import AddTestCaseModal from '../features/AddTestCaseModal';
import MilestoneSelector from '../shared/MilestoneSelector';
import '../../App.css';

interface TestPlanFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingScope: TestPlan | null;
  productId: string;
  features: Feature[];
  components: Component[];
  platforms: PlatformItem[];
  onSave: () => Promise<void>; // Callback to refresh test plans list
  onFeaturesUpdated?: () => Promise<void>; // Callback to refresh features list
  onComponentsUpdated?: () => Promise<void>; // Callback to refresh components list
  showMessage: (msg: AppMessage) => void;
  setMessage: MessageSetter; // For showTempMessage
  projectCode?: string; // Project code prefix for TC ID display (e.g., "FS" -> "FS-TC-42")
}

const TestPlanFormModal: React.FC<TestPlanFormModalProps> = ({
  isOpen,
  onClose,
  editingScope,
  productId,
  features,
  components,
  platforms,
  onSave,
  onFeaturesUpdated,
  onComponentsUpdated,
  showMessage,
  setMessage,
  projectCode
}) => {
  const [formData, setFormData] = useState<Partial<TestPlan>>({
    title: '',
    userStory: '',
    requirements: '',
    riskAreas: '',
    testingPlan: [],
    productId: '',
    components: [],
    componentNotes: {},
    milestone: '',
    referenceUrl: '',
    questions: [],
    timeEstimation: undefined
  });
  
  // State for time estimation input (as formatted string)
  const [timeEstimationInput, setTimeEstimationInput] = useState<string>('');
  const [showAddComponentModal, setShowAddComponentModal] = useState(false);
  const [showCreateComponentModal, setShowCreateComponentModal] = useState(false);
  const [showFeatureFormInComponentModal, setShowFeatureFormInComponentModal] = useState(false);
  const [newComponentName, setNewComponentName] = useState('');
  const [newComponentDescription, setNewComponentDescription] = useState('');
  const [newComponentFeatureId, setNewComponentFeatureId] = useState<string>('');
  const [newComponentMilestone, setNewComponentMilestone] = useState<string>('');
  const [newFeatureName, setNewFeatureName] = useState('');
  const [newFeatureDescription, setNewFeatureDescription] = useState('');
  const [testCases, setTestCases] = useState<TestCase[]>([]);
  const [showAddTestCaseModal, setShowAddTestCaseModal] = useState(false);
  const [selectedComponentForTestCase, setSelectedComponentForTestCase] = useState<string | null>(null);
  const milestones = useMilestones(productId);

  // Initialize form data when modal opens or editingScope changes
  useEffect(() => {
    if (isOpen) {
      if (editingScope) {
        setFormData({
          title: editingScope.title,
          userStory: editingScope.userStory || '',
          requirements: editingScope.requirements || '',
          riskAreas: editingScope.riskAreas || '',
          testingPlan: editingScope.testingPlan || [],
          productId: editingScope.productId || productId,
          components: editingScope.components || [],
          componentNotes: editingScope.componentNotes || {},
          milestone: editingScope.milestone || '',
          referenceUrl: editingScope.referenceUrl || '',
          questions: editingScope.questions || [],
          status: editingScope.status || 'draft',
          timeEstimation: editingScope.timeEstimation
        });
        // Set time estimation input for display
        if (editingScope.timeEstimation !== undefined) {
          setTimeEstimationInput(formatDurationHHMMSS(editingScope.timeEstimation));
        } else {
          setTimeEstimationInput('');
        }
      } else {
        setFormData({
          title: '',
          userStory: '',
          requirements: '',
          riskAreas: '',
          testingPlan: [],
          productId: productId,
          components: [],
          componentNotes: {},
          milestone: '',
          referenceUrl: '',
          questions: [],
          status: 'draft',
          timeEstimation: undefined
        });
        setTimeEstimationInput('');
      }
    }
  }, [isOpen, editingScope, productId]);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  // Auto-resize textareas when questions change
  useEffect(() => {
    if (isOpen && formData.questions) {
      // Use setTimeout to ensure DOM is updated
      setTimeout(() => {
        const textareas = document.querySelectorAll('textarea[data-question-textarea], textarea[data-answer-textarea]');
        textareas.forEach((textarea) => {
          const el = textarea as HTMLTextAreaElement;
          el.style.height = 'auto';
          el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
        });
      }, 0);
    }
  }, [isOpen, formData.questions]);

  // Fetch test cases for selected components
  useEffect(() => {
    if (isOpen && formData.components && formData.components.length > 0) {
      const fetchTestCases = async () => {
        try {
          const allTestCases: TestCase[] = [];
          for (const componentId of formData.components || []) {
            try {
              const componentTestCases = await apiRequest<TestCase[]>(
                `/api/testcases?componentId=${componentId}`,
                {},
                { useAuth: true }
              );
              if (Array.isArray(componentTestCases)) {
                allTestCases.push(...componentTestCases);
              }
            } catch (error) {
              console.error(`Error fetching test cases for component ${componentId}:`, error);
            }
          }
          setTestCases(allTestCases);
        } catch (error) {
          console.error('Error fetching test cases:', error);
          setTestCases([]);
        }
      };
      fetchTestCases();
    } else {
      setTestCases([]);
    }
  }, [isOpen, formData.components]);

  // Get test cases for a specific component
  const getTestCasesForComponent = (componentId: string): TestCase[] => {
    return testCases.filter(tc => tc.componentId === componentId);
  };

  // Handle test case added
  const handleTestCaseAdded = async (testCase: TestCase) => {
    // Refresh test cases from API to ensure we have the latest data
    if (formData.components && formData.components.length > 0) {
      try {
        const allTestCases: TestCase[] = [];
        for (const componentId of formData.components || []) {
          try {
            const componentTestCases = await apiRequest<TestCase[]>(
              `/api/testcases?componentId=${componentId}`,
              {},
              { useAuth: true }
            );
            if (Array.isArray(componentTestCases)) {
              allTestCases.push(...componentTestCases);
            }
          } catch (error) {
            console.error(`Error fetching test cases for component ${componentId}:`, error);
          }
        }
        setTestCases(allTestCases);
      } catch (error) {
        console.error('Error refreshing test cases:', error);
        // Fallback: add the new test case to local state
        setTestCases(prev => [...prev, testCase]);
      }
    }
    // Refresh components list to ensure sync with Features Management
    if (onComponentsUpdated) {
      await onComponentsUpdated();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePlatformToggle = (platform: Platform) => {
    setFormData(prev => {
      const currentPlan = prev.testingPlan || [];
      const isSelected = currentPlan.includes(platform);
      return {
        ...prev,
        testingPlan: isSelected
          ? currentPlan.filter(p => p !== platform)
          : [...currentPlan, platform]
      };
    });
  };


  const handleRemoveComponent = (componentId: string) => {
    setFormData(prev => {
      const newNotes = { ...(prev.componentNotes || {}) };
      delete newNotes[componentId];
      return {
        ...prev,
        components: (prev.components || []).filter(id => id !== componentId),
        componentNotes: newNotes
      };
    });
  };

  const handleComponentNoteChange = (componentId: string, note: string) => {
    setFormData(prev => ({
      ...prev,
      componentNotes: {
        ...(prev.componentNotes || {}),
        [componentId]: note
      }
    }));
  };

  const handleSave = async () => {
    if (!formData.title?.trim()) {
      showTempMessage(setMessage, { type: 'error', text: 'Title is required' });
      return;
    }
    if (!productId) {
      showTempMessage(setMessage, { type: 'error', text: 'Please select a product' });
      return;
    }
    if (!formData.milestone || !formData.milestone.trim()) {
      showTempMessage(setMessage, { type: 'error', text: 'Milestone is required' });
      return;
    }

    try {
      const scopeData = {
        ...formData,
        productId: productId,
        status: formData.status || (editingScope ? (editingScope.status || 'draft') : 'draft') // Use form status or default
      };

      if (editingScope) {
        await apiRequest(`/api/testplans/${editingScope.id}`, {
          method: 'PUT',
          body: JSON.stringify(scopeData),
          headers: { 'Content-Type': 'application/json' }
        }, { useAuth: true });
        showTempMessage(setMessage, { type: 'success', text: 'Test plan updated successfully' });
      } else {
        await apiRequest('/api/testplans', {
          method: 'POST',
          body: JSON.stringify(scopeData),
          headers: { 'Content-Type': 'application/json' }
        }, { useAuth: true });
        showTempMessage(setMessage, { type: 'success', text: 'Test plan created successfully' });
      }

      await onSave(); // Refresh test plans list
      onClose();
    } catch (error: any) {
      showTempMessage(setMessage, { type: 'error', text: error.message || 'Failed to save test plan' });
    }
  };

  const handleComponentsSelected = (featureIds: string[], componentIds: string[]) => {
    setFormData(prev => {
      const currentComponents = prev.components || [];
      const newComponents = [...currentComponents];
      const newNotes = { ...(prev.componentNotes || {}) };

      componentIds.forEach(componentId => {
        if (!newComponents.includes(componentId)) {
          newComponents.push(componentId);
          if (!newNotes[componentId]) {
            newNotes[componentId] = '';
          }
        }
      });

      return {
        ...prev,
        components: newComponents,
        componentNotes: newNotes
      };
    });
  };

  const handleFeatureCreated = async (feature: Feature) => {
    // Refresh features list if callback provided
    if (onFeaturesUpdated) {
      await onFeaturesUpdated();
    }
  };

  const handleCreateFeatureInline = async () => {
    if (!newFeatureName.trim() || !productId) {
      showTempMessage(setMessage, { type: 'error', text: 'Feature name is required' });
      return;
    }

    try {
      const newFeature = await apiRequest<Feature>('/api/features', {
        method: 'POST',
        body: JSON.stringify({
          name: newFeatureName.trim(),
          productId: productId,
          description: newFeatureDescription.trim() || undefined
        }),
        headers: { 'Content-Type': 'application/json' }
      }, { useAuth: true });

      await handleFeatureCreated(newFeature);
      // Auto-select the newly created feature
      setNewComponentFeatureId(newFeature.id);
      // Switch back to component form
      setShowFeatureFormInComponentModal(false);
      setNewFeatureName('');
      setNewFeatureDescription('');
      showTempMessage(setMessage, { type: 'success', text: 'Feature created successfully' });
    } catch (error: any) {
      showTempMessage(setMessage, { type: 'error', text: error.message || 'Failed to create feature' });
    }
  };

  const handleCreateComponent = async () => {
    if (!newComponentName.trim() || !newComponentFeatureId || !newComponentMilestone.trim()) {
      showTempMessage(setMessage, { type: 'error', text: 'Component name, feature, and milestone are required' });
      return;
    }

    try {
      const newComponent = await apiRequest<Component>('/api/components', {
        method: 'POST',
        body: JSON.stringify({
          name: newComponentName.trim(),
          featureId: newComponentFeatureId,
          description: newComponentDescription.trim() || undefined,
          milestone: newComponentMilestone.trim()
        }),
        headers: { 'Content-Type': 'application/json' }
      }, { useAuth: true });

      // Add the new component to the form data
      setFormData(prev => ({
        ...prev,
        components: [...(prev.components || []), newComponent.id],
        componentNotes: {
          ...(prev.componentNotes || {}),
          [newComponent.id]: ''
        }
      }));

      setNewComponentName('');
      setNewComponentDescription('');
      setNewComponentFeatureId('');
      setNewComponentMilestone('');
      setShowCreateComponentModal(false);
      showTempMessage(setMessage, { type: 'success', text: 'Component created successfully' });
      
      // Refresh components list if callback provided
      if (onComponentsUpdated) {
        await onComponentsUpdated();
      }
    } catch (error: any) {
      showTempMessage(setMessage, { type: 'error', text: error.message || 'Failed to create component' });
    }
  };

  if (!isOpen) return null;

  const selectedComponents = (formData.components || []).map(id => 
    components.find(c => c.id === id)
  ).filter(Boolean) as Component[];

  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          overflow: 'auto',
          padding: '2rem'
        }}
      >
        <div
          className="card"
          style={{ 
            maxWidth: '900px', 
            width: '100%', 
            maxHeight: '90vh',
            overflow: 'auto',
            zIndex: 1001,
            position: 'relative'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h2 className="section-title">{editingScope ? 'Edit Test Plan' : 'Create New Test Plan'}</h2>
            <button
              onClick={onClose}
              className="btn btn-ghost btn-icon"
              style={{ fontSize: '1.5rem' }}
            >
              ×
            </button>
          </div>
          
          {/* Top Row: Title and Status */}
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
            <div className="form-group" style={{ flex: 2, marginBottom: 0 }}>
              <label style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                Title <span style={{ color: 'var(--danger)', fontWeight: '700', marginLeft: '0.25rem' }}>*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title || ''}
                onChange={handleInputChange}
                placeholder="Enter test plan title"
                required
                style={{
                  width: '100%',
                  borderLeft: '3px solid var(--primary)',
                  paddingLeft: 'calc(0.75rem - 2px)',
                  background: 'linear-gradient(to right, rgba(2, 132, 199, 0.03) 0%, transparent 3px)'
                }}
              />
            </div>
            {(editingScope?.status === 'draft' || editingScope?.status === 'active' || !editingScope) && (
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                  Status <span style={{ color: 'var(--danger)', fontWeight: '700', marginLeft: '0.25rem' }}>*</span>
                </label>
                <select
                  value={formData.status || (editingScope?.status || 'draft')}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      status: e.target.value as 'draft' | 'active'
                    }));
                  }}
                  required
                  style={{
                    width: '100%',
                    padding: '0.5rem 2.5rem 0.5rem calc(0.5rem - 2px)',
                    fontSize: '0.875rem',
                    border: '1px solid var(--grid-line)',
                    borderLeft: '3px solid var(--primary)',
                    borderRadius: '4px',
                    background: 'linear-gradient(to right, rgba(2, 132, 199, 0.03) 0%, transparent 3px), url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' fill=\'%2300d4ff\' viewBox=\'0 0 16 16\'%3E%3Cpath d=\'M8 11L3 6h10l-5 5z\'/%3E%3C/svg%3E") no-repeat right 1rem center',
                    appearance: 'none'
                  }}
                >
                  <option value="draft">Draft (Still being worked on)</option>
                  <option value="active">Active (Ready for testing)</option>
                </select>
              </div>
            )}
          </div>

          {/* Second Row: Reference URL (left) and Milestone (right, below Status) */}
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
            <div className="form-group" style={{ flex: 2, marginBottom: 0 }}>
              <label>Reference URL</label>
              <input
                type="url"
                name="referenceUrl"
                value={formData.referenceUrl || ''}
                onChange={handleInputChange}
                placeholder="e.g., https://github.com/org/repo/pull/123"
                style={{ width: '100%' }}
              />
            </div>
            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
              <label style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                Milestone <span style={{ color: 'var(--danger)', fontWeight: '700', marginLeft: '0.25rem' }}>*</span>
              </label>
              <div style={{
                borderLeft: '3px solid var(--primary)',
                paddingLeft: 'calc(0.5rem - 2px)',
                background: 'linear-gradient(to right, rgba(2, 132, 199, 0.03) 0%, transparent 3px)',
                borderRadius: '4px'
              }}>
                <MilestoneSelector
                  value={formData.milestone || ''}
                  onChange={(milestone) => {
                    setFormData(prev => ({ ...prev, milestone }));
                  }}
                  productId={productId}
                  placeholder="e.g., Q1 2024, Sprint 5, Release 2.0"
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>User Story</label>
            <textarea
              name="userStory"
              value={formData.userStory || ''}
              onChange={handleInputChange}
              placeholder="What problem is this solving?"
              rows={3}
              style={{ width: '100%' }}
            />
          </div>

          <div className="form-group">
            <label>Requirements</label>
            <textarea
              name="requirements"
              value={formData.requirements || ''}
              onChange={handleInputChange}
              placeholder="List the requirements for this feature"
              rows={4}
              style={{ width: '100%' }}
            />
          </div>

          <div className="form-group">
            <label>Risk Areas</label>
            <textarea
              name="riskAreas"
              value={formData.riskAreas || ''}
              onChange={handleInputChange}
              placeholder="Identify potential risk areas"
              rows={3}
              style={{ width: '100%' }}
            />
          </div>

          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <label style={{ margin: 0 }}>Questions & Answers</label>
              <button
                type="button"
                onClick={() => {
                  const newQuestion: QuestionAnswer = {
                    id: `q-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                    question: '',
                    answer: ''
                  };
                  setFormData(prev => ({
                    ...prev,
                    questions: [...(prev.questions || []), newQuestion]
                  }));
                }}
                className="btn btn-primary btn-sm"
                style={{ fontSize: '0.875rem', padding: '0.375rem 0.75rem' }}
              >
                + Add Question
              </button>
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
              Add questions for QA collaboration before development. Each question has its own answer field.
            </div>
            
            {(formData.questions || []).length === 0 ? (
              <div className="card" style={{ padding: '1.5rem', textAlign: 'center', backgroundColor: 'var(--surface)', border: '1px dashed var(--grid-line)' }}>
                <p style={{ color: 'var(--text-muted)', margin: 0 }}>
                  No questions yet. Click "Add Question" to get started.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {(formData.questions || []).map((qa, index) => (
                  <div
                    key={qa.id}
                    className="card"
                    style={{
                      padding: '1rem',
                      backgroundColor: 'var(--surface)',
                      border: '1px solid var(--grid-line)',
                      borderRadius: '6px'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                      <div style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>
                        Question {index + 1}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            questions: (prev.questions || []).filter(q => q.id !== qa.id)
                          }));
                        }}
                        className="btn btn-ghost btn-icon"
                        style={{ fontSize: '1.25rem', padding: '0.25rem', color: 'var(--error)' }}
                        title="Remove question"
                      >
                        ×
                      </button>
                    </div>
                    <div style={{ marginBottom: '0.75rem' }}>
                      <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem', color: 'var(--text-secondary)' }}>
                        Question
                      </label>
                      <textarea
                        data-question-textarea
                        value={qa.question}
                        onChange={(e) => {
                          const updatedQuestions = [...(formData.questions || [])];
                          updatedQuestions[index] = { ...qa, question: e.target.value };
                          setFormData(prev => ({ ...prev, questions: updatedQuestions }));
                          // Auto-resize
                          e.target.style.height = 'auto';
                          e.target.style.height = `${Math.min(e.target.scrollHeight, 200)}px`;
                        }}
                        onInput={(e) => {
                          // Auto-resize on input
                          const target = e.target as HTMLTextAreaElement;
                          target.style.height = 'auto';
                          target.style.height = `${Math.min(target.scrollHeight, 200)}px`;
                        }}
                        placeholder="Enter your question..."
                        rows={1}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          fontSize: '0.875rem',
                          border: '1px solid var(--grid-line)',
                          borderRadius: '4px',
                          fontFamily: 'inherit',
                          resize: 'none',
                          overflow: 'hidden',
                          minHeight: '2.5rem',
                          maxHeight: '200px'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem', color: 'var(--text-secondary)' }}>
                        Answer
                      </label>
                      <textarea
                        data-answer-textarea
                        value={qa.answer || ''}
                        onChange={(e) => {
                          const updatedQuestions = [...(formData.questions || [])];
                          updatedQuestions[index] = { ...qa, answer: e.target.value };
                          setFormData(prev => ({ ...prev, questions: updatedQuestions }));
                          // Auto-resize
                          e.target.style.height = 'auto';
                          e.target.style.height = `${Math.min(e.target.scrollHeight, 200)}px`;
                        }}
                        onInput={(e) => {
                          // Auto-resize on input
                          const target = e.target as HTMLTextAreaElement;
                          target.style.height = 'auto';
                          target.style.height = `${Math.min(target.scrollHeight, 200)}px`;
                        }}
                        placeholder="Answer will appear here..."
                        rows={1}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          fontSize: '0.875rem',
                          border: '1px solid var(--grid-line)',
                          borderRadius: '4px',
                          fontFamily: 'inherit',
                          backgroundColor: qa.answer ? 'var(--surface)' : 'var(--surface-alt)',
                          resize: 'none',
                          overflow: 'hidden',
                          minHeight: '2.5rem',
                          maxHeight: '200px'
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

            <div className="form-group">
              <label>Testing Plan (Select Platforms)</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {platforms.map(platform => {
                  const platformName = platform.name as Platform;
                  const isSelected = (formData.testingPlan || []).includes(platformName);
                  return (
                    <button
                      key={platform.id}
                      type="button"
                      onClick={() => handlePlatformToggle(platformName)}
                      className={isSelected ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
                    >
                      {platform.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="form-group">
              <label>Time Estimation (seconds or HH:MM:SS)</label>
              <input
                type="text"
                name="timeEstimation"
                value={timeEstimationInput}
                onChange={(e) => {
                  const value = e.target.value;
                  setTimeEstimationInput(value);
                  const parsed = parseDurationHHMMSS(value);
                  setFormData(prev => ({
                    ...prev,
                    timeEstimation: parsed
                  }));
                }}
                placeholder="e.g., 3600 or 01:00:00 (1 hour)"
                style={{ 
                  width: '100%',
                  fontFamily: 'monospace'
                }}
              />
              {formData.timeEstimation !== undefined && (
                <div style={{ 
                  fontSize: '0.75rem', 
                  color: 'var(--text-secondary)', 
                  marginTop: '0.25rem' 
                }}>
                  {formatDurationHHMMSS(formData.timeEstimation)} ({formData.timeEstimation} seconds)
                </div>
              )}
            </div>

            {/* Components Section */}
            <div className="form-group">
              <label>Components</label>
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => setShowAddComponentModal(true)}
                  className="btn btn-primary btn-sm"
                >
                  Add Components
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateComponentModal(true)}
                  className="btn btn-secondary btn-sm"
                >
                  Create New Component
                </button>
              </div>

              {/* Selected Components List */}
              {selectedComponents.length === 0 ? (
                <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
                  <p className="text-secondary">
                    No components added yet. Use the "Add Components" button to add components.
                  </p>
                </div>
              ) : (
                <div style={{ marginBottom: '1rem' }}>
                  {selectedComponents.map(component => {
                    const feature = features.find(f => f.id === component.featureId);
                    const componentTestCases = getTestCasesForComponent(component.id);
                    return (
                      <div
                        key={component.id}
                        className="card"
                        style={{ padding: '1rem', marginBottom: '0.5rem' }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                          <div>
                            <strong style={{ color: 'var(--text-primary)' }}>{component.name}</strong>
                            {feature && <span className="text-secondary ml-sm">({feature.name})</span>}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveComponent(component.id)}
                            className="btn btn-ghost btn-icon"
                            style={{ fontSize: '1.25rem' }}
                          >
                            ×
                          </button>
                        </div>
                        <textarea
                          placeholder="Add notes for this component..."
                          value={formData.componentNotes?.[component.id] || ''}
                          onChange={(e) => handleComponentNoteChange(component.id, e.target.value)}
                          rows={2}
                          className="form-control"
                          style={{ marginBottom: '0.75rem' }}
                        />
                        
                        {/* Test Cases Section */}
                        <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid var(--grid-line)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <label style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)', margin: 0 }}>
                              Test Cases ({componentTestCases.length})
                            </label>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedComponentForTestCase(component.id);
                                setShowAddTestCaseModal(true);
                              }}
                              className="btn btn-primary btn-sm"
                              style={{ fontSize: '0.75rem', padding: '0.375rem 0.75rem' }}
                            >
                              + Add Test Case
                            </button>
                          </div>
                          {componentTestCases.length === 0 ? (
                            <div style={{ padding: '0.75rem', backgroundColor: 'var(--surface-alt)', borderRadius: '4px', fontSize: '0.875rem', color: 'var(--text-muted)', textAlign: 'center' }}>
                              No test cases yet. Click "Add Test Case" to create one.
                            </div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                              {componentTestCases.map(testCase => {
                                return (
                                  <div
                                    key={testCase.id}
                                    style={{
                                      padding: '0.75rem',
                                      backgroundColor: 'var(--surface-alt)',
                                      borderRadius: '4px',
                                      border: '1px solid var(--grid-line)'
                                    }}
                                  >
                                    <div style={{ 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      gap: '0.5rem',
                                      marginBottom: testCase.description ? '0.25rem' : 0
                                    }}>
                                      {testCase.testCaseNumber && (
                                        <span style={{
                                          fontSize: '0.7rem',
                                          fontWeight: 600,
                                          padding: '0.15rem 0.35rem',
                                          backgroundColor: 'var(--cyan-glow)',
                                          color: 'var(--surface)',
                                          borderRadius: '3px',
                                          fontFamily: 'monospace',
                                          flexShrink: 0
                                        }}>
                                          {projectCode ? `${projectCode}-TC-${testCase.testCaseNumber}` : `TC-${testCase.testCaseNumber}`}
                                        </span>
                                      )}
                                      <div style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--text-primary)', flex: 1 }}>
                                        {testCase.title}
                                      </div>
                                      {testCase.steps && testCase.steps.length > 0 && (
                                        <span style={{ 
                                          fontSize: '0.65rem', 
                                          padding: '0.1rem 0.3rem', 
                                          backgroundColor: 'rgba(0, 212, 255, 0.2)', 
                                          color: 'var(--cyan-glow)', 
                                          borderRadius: '3px',
                                          flexShrink: 0
                                        }}>
                                          {testCase.steps.length} steps
                                        </span>
                                      )}
                                    </div>
                                    {testCase.description && (
                                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem', whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                                        {testCase.description}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

          <div className="button-group" style={{ marginTop: '1.5rem' }}>
            <button
              onClick={handleSave}
              className="btn btn-primary"
            >
              {editingScope ? 'Update Test Plan' : 'Create Test Plan'}
            </button>
            <button
              onClick={onClose}
              className="btn btn-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      {/* Add Component Modal */}
      {showAddComponentModal && (
        <AddComponentModal
          session={undefined}
          allFeatures={features}
          allComponents={components}
          allPlatforms={platforms}
          isOpen={showAddComponentModal}
          onClose={() => setShowAddComponentModal(false)}
          onComponentsAdded={handleComponentsSelected}
          showMessage={showMessage}
          alreadySelectedComponentIds={formData.components || []}
          milestone={formData.milestone}
          milestones={milestones}
        />
      )}


      {/* Create Component Modal */}
      {showCreateComponentModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
          }}
          onClick={() => {
            setShowCreateComponentModal(false);
            setShowFeatureFormInComponentModal(false);
            setNewComponentName('');
            setNewComponentDescription('');
            setNewComponentFeatureId('');
            setNewComponentMilestone('');
            setNewFeatureName('');
            setNewFeatureDescription('');
          }}
        >
          <div
            className="card"
            style={{ maxWidth: '500px', width: '90%', zIndex: 2001 }}
            onClick={(e) => e.stopPropagation()}
          >
            {showFeatureFormInComponentModal ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                  <h3 className="section-title">Create New Feature</h3>
                  <button
                    onClick={() => {
                      setShowFeatureFormInComponentModal(false);
                      setNewFeatureName('');
                      setNewFeatureDescription('');
                    }}
                    className="btn btn-ghost btn-icon"
                    style={{ fontSize: '1.5rem' }}
                  >
                    ×
                  </button>
                </div>
                <div className="form-group">
                  <label>Feature Name *</label>
                  <input
                    type="text"
                    value={newFeatureName}
                    onChange={(e) => setNewFeatureName(e.target.value)}
                    placeholder="Enter feature name"
                    className="form-control"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newFeatureName.trim() && productId) {
                        e.preventDefault();
                        handleCreateFeatureInline();
                      }
                    }}
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={newFeatureDescription}
                    onChange={(e) => setNewFeatureDescription(e.target.value)}
                    placeholder="Enter feature description"
                    rows={3}
                    className="form-control"
                  />
                </div>
                <div className="button-group" style={{ marginTop: '1.5rem' }}>
                  <button
                    onClick={handleCreateFeatureInline}
                    disabled={!newFeatureName.trim()}
                    className="btn btn-primary"
                    style={{
                      opacity: newFeatureName.trim() ? 1 : 0.5,
                      cursor: newFeatureName.trim() ? 'pointer' : 'not-allowed'
                    }}
                  >
                    Create Feature
                  </button>
                  <button
                    onClick={() => {
                      setShowFeatureFormInComponentModal(false);
                      setNewFeatureName('');
                      setNewFeatureDescription('');
                    }}
                    className="btn btn-secondary"
                  >
                    Back to Component
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                  <h3 className="section-title">Create New Component</h3>
                  <button
                    onClick={() => {
                      setShowCreateComponentModal(false);
                      setNewComponentName('');
                      setNewComponentDescription('');
                      setNewComponentFeatureId('');
                      setNewComponentMilestone('');
                    }}
                    className="btn btn-ghost btn-icon"
                    style={{ fontSize: '1.5rem' }}
                  >
                    ×
                  </button>
                </div>
                <div className="form-group">
                  <label>Component Name *</label>
                  <input
                    type="text"
                    value={newComponentName}
                    onChange={(e) => setNewComponentName(e.target.value)}
                    placeholder="Enter component name"
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>Feature *</label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
                    <select
                      value={newComponentFeatureId}
                      onChange={(e) => setNewComponentFeatureId(e.target.value)}
                      className="form-control"
                      style={{ 
                        flex: 1,
                        paddingRight: '2.5rem',
                        appearance: 'none',
                        backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%2300d4ff' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10l-5 5z'/%3E%3C/svg%3E\")",
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 1rem center',
                      }}
                    >
                      <option value="">Select a feature</option>
                      {features.map(feature => (
                        <option key={feature.id} value={feature.id}>{feature.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowFeatureFormInComponentModal(true)}
                      className="btn btn-secondary btn-sm"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      + New Feature
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={newComponentDescription}
                    onChange={(e) => setNewComponentDescription(e.target.value)}
                    placeholder="Enter component description"
                    rows={3}
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>Milestone <span className="text-danger">*</span></label>
                  <MilestoneSelector
                    value={newComponentMilestone}
                    onChange={setNewComponentMilestone}
                    productId={productId}
                    placeholder="Select or create milestone"
                    required
                  />
                </div>
                <div className="button-group" style={{ marginTop: '1.5rem' }}>
                  <button
                    onClick={handleCreateComponent}
                    disabled={!newComponentName.trim() || !newComponentFeatureId || !newComponentMilestone.trim()}
                    className="btn btn-primary"
                    style={{ 
                      opacity: (newComponentName.trim() && newComponentFeatureId && newComponentMilestone.trim()) ? 1 : 0.5,
                      cursor: (newComponentName.trim() && newComponentFeatureId && newComponentMilestone.trim()) ? 'pointer' : 'not-allowed'
                    }}
                  >
                    Create Component
                  </button>
                  <button
                    onClick={() => {
                      setShowCreateComponentModal(false);
                      setNewComponentName('');
                      setNewComponentDescription('');
                      setNewComponentFeatureId('');
                      setNewComponentMilestone('');
                    }}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Add Test Case Modal */}
      {showAddTestCaseModal && (
        <AddTestCaseModal
          allComponents={components}
          allFeatures={features}
          isOpen={showAddTestCaseModal}
          onClose={() => {
            setShowAddTestCaseModal(false);
            setSelectedComponentForTestCase(null);
          }}
          onTestCaseAdded={handleTestCaseAdded}
          showMessage={showMessage}
          prefillData={{
            componentId: selectedComponentForTestCase || undefined
          }}
        />
      )}
    </>
  );
};

export default TestPlanFormModal;

